import {Grid, makeStyles} from '@material-ui/core';
import {isLoaded, Loaded} from 'Core/models/Async';
import {List as IList} from 'immutable';
import {useQuoteFormValidation} from 'Quoting/components/hooks/useQuoteFormValidation';
import {QuoteFormElement} from 'Quoting/components/shared/QuoteFormElement';
import {removeQuoteFormErrorEntry, updateMasterPolicyQuote, updateQuoteFormErrors} from 'Quoting/ducks/createQuote';
import {CensusClassDTO} from 'Quoting/models/DTOs/Opportunity';
import {A360ProductModel} from 'Quoting/models/Quoting';
import {getProductCdFromModel} from 'Quoting/utils/helpers';
import {
  getByKeyPath,
  getCensusClassURI,
  isA360HealthProduct,
  isA360LifeProduct,
  isDefined,
  safeRenderValue,
} from 'Quoting/utils/utilities';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {GPLU_COVERAGE_CODE_TO_COVERAGE_NAME_MAP, GPLUCoverageCode} from '../../PlanTemplates/GWL/gpluConstants';
import {handleCensusClassSelectionChange, handleCensusClassSelectionChangeLife} from './censusClassUtils';

const useClassSelectionStyles = makeStyles(() => ({
  coverageHeader: {
    color: '#2863a9',
  },
  classSelectionRow: {
    paddingLeft: '1.5rem',
    fontWeight: 600,
    fontSize: '1rem',
  },
  error: {
    fontWeight: 400,
    color: 'red',
    fontSize: '0.875rem',
  },
  censusClassList: {
    paddingTop: '1.25rem',
    paddingBottom: '1rem',
  },
  classSelectionLabel: {
    display: 'flex',
    flexDirection: 'column',
    fontWeight: 600,
    color: '#2F3847',

    '& > :first-child': {
      marginBottom: '0.5rem',
    },
  },
}));

const ClassSelectionLabel = ({censusClass, className}: {censusClass: CensusClassDTO; className: string}) => (
  <div className={className}>
    <span>Class {safeRenderValue(censusClass.number)}</span>
    <span>{safeRenderValue(censusClass.name)}</span>
  </div>
);

const disabledCoverageList: GPLUCoverageCode[] = [
  GPLUCoverageCode.SPOUSE_WHOLE_LIFE,
  GPLUCoverageCode.CHILD_TERM_LIFE,
  GPLUCoverageCode.ACCEL_DEATH_BENEFIT_LIFE,
] as const;

export const CensusClassSelection = (coverageCode?: GPLUCoverageCode) => {
  const classes = useClassSelectionStyles();
  const {
    quote,
    customerCensusClasses,
    selectedPlan,
    selectedCoverageIndex,
    quoteFormErrors,
    selectedLifeCoverage,
  } = useSelector((state: ReducerState) => state.quoteForm);
  const dispatch = useDispatch();

  const coverageDefIndex = selectedCoverageIndex ?? 0;

  const productModel = isLoaded(quote) ? (quote.Data?.get('_modelName') as A360ProductModel) : undefined;

  const ClassGroupsErrorKey = productModel
    ? `${getProductCdFromModel(productModel)}MasterPolicySummary.classGroups`
    : '';

  const ClassGroupsLinksErrorKey = isA360HealthProduct(productModel)
    ? `${getProductCdFromModel(productModel)}CoverageDefinition.coverageGroupsLinks`
    : `plan.${getProductCdFromModel(productModel)}CoverageDefinition.coverageGroupsLinks__${selectedPlan?.planKey}`;

  const coverageGroupLinksPath = `benefitPackagingDetail.plans[${selectedPlan?.planIndex}].coverageDefs[${coverageDefIndex}].coverageGroupsLinks`;

  const handleValidationOnBlur = useQuoteFormValidation();
  const hasError =
    (isA360HealthProduct(productModel) &&
      quoteFormErrors &&
      (Boolean(quoteFormErrors?.[ClassGroupsErrorKey]) || Boolean(quoteFormErrors?.[ClassGroupsLinksErrorKey])) &&
      isLoaded(quote) &&
      (getByKeyPath(quote?.Data, coverageGroupLinksPath) as IList<any>)?.size === 0) ||
    (isA360LifeProduct(productModel) && Boolean(quoteFormErrors?.[ClassGroupsLinksErrorKey]));

  const error = hasError ? quoteFormErrors?.[ClassGroupsErrorKey] ?? quoteFormErrors?.[ClassGroupsLinksErrorKey] : null;

  const handleCensusClassSelection = (censusClassRootId: string) => {
    if (isLoaded(quote)) {
      const updatedQuoteData = isA360HealthProduct(productModel)
        ? handleCensusClassSelectionChange(censusClassRootId, quote.Data, selectedPlan?.planIndex)
        : handleCensusClassSelectionChangeLife(censusClassRootId, quote.Data, selectedPlan?.planIndex);

      if (isDefined(updatedQuoteData)) {
        dispatch(updateMasterPolicyQuote(new Loaded(updatedQuoteData)));

        // set error if census class is not selected
        if (isA360LifeProduct(productModel)) {
          const censusClassesList = getByKeyPath(updatedQuoteData, coverageGroupLinksPath);
          if (!IList.isList(censusClassesList) || censusClassesList?.size === 0) {
            dispatch(
              updateQuoteFormErrors({
                [ClassGroupsLinksErrorKey]: "At least one 'Classification Group' should be added to the quote.",
              })
            );
          } else {
            dispatch(removeQuoteFormErrorEntry(ClassGroupsLinksErrorKey));
          }
        }
      }
    }
  };

  const isCensusClassSelectedForPlan = (censusClassRootId?: string) => {
    const quoteData = isLoaded(quote) ? quote.Data : null;

    if (quoteData) {
      const classId = quoteData
        .get('classGroups')
        ?.find(c => getByKeyPath(c, 'classGroupExtLink._uri') === getCensusClassURI(censusClassRootId))
        ?.get('_key')
        ?.get('id');

      const coverageGroupLinks = getByKeyPath(
        quoteData,
        `benefitPackagingDetail.plans[${selectedPlan?.planIndex}].coverageDefs[${coverageDefIndex}].coverageGroupsLinks`
      );

      return (coverageGroupLinks as IList<any>)?.some(cgl => getByKeyPath(cgl, 'groupLink._ref') === classId);
    }

    return false;
  };

  return (
    <div className={classes.classSelectionRow}>
      {!isA360HealthProduct(productModel) && (
        <h2 className={classes.coverageHeader}>
          {GPLU_COVERAGE_CODE_TO_COVERAGE_NAME_MAP[selectedLifeCoverage as GPLUCoverageCode]}
        </h2>
      )}
      <p>Class Selection</p>
      {hasError && error && <span className={classes.error}>{error}</span>}
      <Grid container className={classes.censusClassList} spacing={4}>
        {Array.isArray(customerCensusClasses) && customerCensusClasses.length > 0 ? (
          customerCensusClasses?.map(censusClass => (
            <Grid item xs={12} sm={6} md={4} key={`class-${censusClass?._key?.rootId}`}>
              <QuoteFormElement
                elementType="FormControl"
                controlType="checkbox"
                label={<ClassSelectionLabel censusClass={censusClass} className={classes.classSelectionLabel} />}
                checked={isCensusClassSelectedForPlan(censusClass?._key?.rootId)}
                onChange={() => handleCensusClassSelection(censusClass._key?.rootId ?? '')}
                onBlur={isA360LifeProduct(productModel) ? undefined : handleValidationOnBlur}
                error={Boolean(quoteFormErrors?.[ClassGroupsErrorKey] || quoteFormErrors?.[ClassGroupsLinksErrorKey])}
                disabled={coverageCode && disabledCoverageList.includes(coverageCode)}
                helperText={undefined}
                key={censusClass?._key?.rootId}
              />
            </Grid>
          ))
        ) : (
          <>No Census Classes Available</>
        )}
      </Grid>
    </div>
  );
};
